import {
  actionCardLastTouchedAt,
  isCardWatchWorking,
  isFollowUpInProgress,
} from '@littlebird/action-cards';

import type { FeedDensity } from '@/features/action-cards-view/feed';
import { pluralize } from '@/lib/date-utils';

import type { PendingActionCardEntry } from './use-waiting-for-you';
import type { ActionCard } from '@littlebird/sdk';

// When the watcher last saw the user working on the card, so a card that leaves Working stays listed
const watcherWorkingAt = (card: ActionCard): number => {
  const workingAt = card.watch?.working_at;
  return workingAt ? Date.parse(workingAt) || 0 : 0;
};

// A parent card counts the activity of its open subtasks, such as a reply drafted from its chat
export const entryLastActiveAt = (entry: PendingActionCardEntry): number =>
  Math.max(
    actionCardLastTouchedAt(entry.card),
    watcherWorkingAt(entry.card),
    ...(entry.children?.open ?? []).map(entryLastActiveAt)
  );

export const HOME_FEED_DENSITY: FeedDensity = 'compact';

// Home sums up the Feed: a section shows at most this many rows, and Open Feed shows the rest
export const HOME_SECTION_ROWS = 3;

// A running or failed card still waits on the user
export const needsYou = (status: string): boolean =>
  status === 'draft' || status === 'running' || status === 'failed';

// The user started a chat about the card, or wrote in one Littlebird started from a to-do
export const hasUserChat = (card: ActionCard): boolean =>
  card.facts?.chat_auto_started !== true &&
  ((card.status === 'draft' && card.facts?.chat_started === true) ||
    isFollowUpInProgress(card));

// A card the watcher saw the user working on counts for a day after it last saw it
const isWorkingCard = (card: ActionCard, nowMs: number): boolean =>
  needsYou(card.status) &&
  (isCardWatchWorking(card, nowMs) || hasUserChat(card));

export const isWorkingEntry = (
  { card, children }: PendingActionCardEntry,
  nowMs: number = Date.now()
): boolean =>
  isWorkingCard(card, nowMs) ||
  !!children?.open.some(({ card }) => isWorkingCard(card, nowMs));

export const feedNeedYouLabel = (count: number) =>
  count === 0
    ? 'Nothing needs you'
    : `${count} ${pluralize(count, 'needs', 'need')} you`;
